import { Paperclip } from "lucide-react";
import { type ChangeEvent, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useImmer } from "use-immer";

import type { AgenticLogicTemplateSummaryDto } from "@intelligems/ig-types";
import { listAllowedChatAttachmentMimes } from "@intelligems/ig-utils";
import { IgChip, IgIconButton } from "@intelligems/ig-theme";
import { Stack } from "@mui/material";

import { useAgenticLogicTemplates } from "@src/api/agentic-logic/list-agentic-logic-templates";
import { useFileUpload } from "@src/api/ai/chat-attachments/use-file-upload";
import { useSyncWorkflow } from "@src/api/ai/execute-workflows/sync-workflow";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { PromptInputProvider } from "@src/components/ai-elements/prompt-input/PromptInputProvider";
import { useProviderAttachments } from "@src/components/ai-elements/prompt-input-context/useProviderAttachments";
import { DiscardChangesDialog } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/DiscardChangesDialog";
import { useUnsavedWorkspaceGuard } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/useUnsavedWorkspaceGuard";
import {
  type AgenticLogicCreationInitialContext,
  buildCodeEntryCreationContext,
} from "@src/features/ai/components/AiChat/agenticLogicCreationContext";
import { useAgenticLogicDataSurface } from "@src/features/ai/pages/agentic-logic/useAgenticLogicDataSurface";
import { resolveAgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/surface";
import { resolveRuleExitMode } from "@src/features/ai/pages/agentic-logic/workspace/ruleExitMode";
import { useDispatch, useSelector } from "@src/store";
import { clearAiChat } from "@src/store/slices/aiChat";
import {
  selectAgenticLogicRuleIdeas,
  setAgenticLogicRuleIdeas,
} from "@src/store/slices/appSettings";
import { setAiChat } from "@src/store/slices/global";

import { CreateRuleDialogView } from "./CreateRuleDialogView";
import { CreateRuleTabEmbeddedView } from "./CreateRuleTabEmbeddedView";
import { CreateRuleTabView } from "./CreateRuleTabView";
import {
  type AgenticLogicRuleIdeasWorkflowVariables,
  useAgenticLogicRuleIdeas,
} from "./useAgenticLogicRuleIdeas";
import { useAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useAgenticLogicSurface";
import { useSurfaceNavigate } from "@src/features/ai/pages/agentic-logic/surface/useSurfaceNavigate";

/**
 * How the creation flow is framed. The rules list opens it as a modal; the
 * `?rule=new` view and the empty rules list render it as the page itself.
 * Only the frame differs — the flow, and every navigation it performs, is one.
 */
export type CreateRulePresentation =
  | { kind: "page" }
  | { kind: "dialog"; open: boolean; onClose: () => void }
  /**
   * Inside a host the rule builder has taken over. The chat rail is already
   * open beside the gallery, and the takeover owns the exit — so this
   * presentation carries no composer and no leave guard of its own.
   */
  | { kind: "embedded" };

const PAGE_PRESENTATION: CreateRulePresentation = { kind: "page" };

export const CreateRuleTab = ({
  presentation = PAGE_PRESENTATION,
}: {
  presentation?: CreateRulePresentation;
} = {}) => {
  const orgId = useGetOrg().data.org.id;
  return (
    <PromptInputProvider>
      <CreateRuleTabContent
        key={orgId}
        orgId={orgId}
        presentation={presentation}
      />
    </PromptInputProvider>
  );
};

/** The creation flow as the rules list's modal. */
export const CreateRuleDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => <CreateRuleTab presentation={{ kind: "dialog", open, onClose }} />;

const CreateRuleTabContent = ({
  orgId,
  presentation,
}: {
  orgId: string;
  presentation: CreateRulePresentation;
}) => {
  const dispatch = useDispatch();
  const surface = useAgenticLogicSurface();
  const navigate = useSurfaceNavigate();
  const location = useLocation();
  const templatesQuery = useAgenticLogicTemplates();
  // The org's dataset keys, so the agent types `sdk.getDataset()` against real
  // keys rather than `never`. Same query the rule workspace observes, so the
  // two share one cache entry and this costs no extra request.
  const { dataSurface } = useAgenticLogicDataSurface();
  // The placement this flow was entered from, when there is one: ideas are
  // written for the surface the author is building for, so a checkout upsell
  // is never offered "on product pages…".
  const placementSurface = surface.placementSurface;
  const persistedRuleIdeas = useSelector(
    selectAgenticLogicRuleIdeas(orgId, placementSurface)
  );
  const ruleIdeasWorkflow =
    useSyncWorkflow<AgenticLogicRuleIdeasWorkflowVariables>();
  const ruleIdeas = useAgenticLogicRuleIdeas(ruleIdeasWorkflow.mutate, {
    initialIdeas: persistedRuleIdeas,
    placementSurface,
    onIdeasChange: (ideas) => {
      dispatch(setAgenticLogicRuleIdeas({ orgId, placementSurface, ideas }));
    },
  });
  const [prompt, setPrompt] = useImmer("");
  const fileUpload = useFileUpload({
    scenario: "chat-attachment",
    maxPerTurn: 5,
  });
  const inDialog = presentation.kind === "dialog";
  const isEmbedded = presentation.kind === "embedded";
  const isDirty =
    !isEmbedded && (prompt.trim().length > 0 || fileUpload.entries.length > 0);
  const exitMode = resolveRuleExitMode({ isDirty });
  // The dialog is not a place you can navigate away from with work in it —
  // closing it is the discard, and its own navigations are the flow leaving.
  // Neither framed flow guards its own exit: closing the dialog IS the
  // discard, and in an embedded host the takeover owns the exit — its
  // `new → rule` hand-off is a search change on the host's own URL, which a
  // search-sensitive guard here would block.
  //
  // The page guards only a typed intake: an empty one loses nothing by
  // leaving, and a prompt on a clean exit is the click-through habit the
  // dirty prompt cannot afford.
  const leaveGuard = useUnsavedWorkspaceGuard(
    !inDialog && !isEmbedded && exitMode !== null,
    { includeSearchChanges: true }
  );

  const openChat = (context: AgenticLogicCreationInitialContext) => {
    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "open", value: true },
        { key: "mode", value: "agentic-logic-creation" },
        { key: "initialContext", value: context },
        { key: "start", value: true },
        { key: "buildingRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
    fileUpload.clearUploads();
    if (presentation.kind === "dialog") {
      presentation.onClose();
    }
    // A build belongs to the creation view: that is the view that becomes the
    // designing skeleton while Atlas works, and the rule workspace once the
    // rule exists. This form has a second home — the rules list renders it
    // inline while the org has no rules yet — and a build started there would
    // run with the page still showing the entry gallery: the rail chatting
    // beside a form that is no longer the story, and the create tool's
    // navigation later caught by this view's own leave guard. Every entry
    // (Ask Atlas, a template card) opens the chat through here, so routing it
    // once here is what keeps them the same flow.
    if (
      resolveAgenticLogicView(location.search, surface.ruleParam).kind !== "new"
    ) {
      leaveGuard.allowNextNavigation();
      navigate(surface.newRulePath);
    }
  };

  // Creating a rule names no existing rule, so there is nothing to read and
  // nothing to await: the context is built from what is already on screen.
  // Going through the async builder deferred `openChat` — and with it the
  // `?rule=new` navigation and the designing canvas — to a microtask after the
  // click, so a build looked like it had done nothing until the tick landed.
  // The template entry beside it has always been synchronous; both are one
  // flow and must start on the same event.
  const startCodeRule = () => {
    openChat(
      buildCodeEntryCreationContext({
        prompt: prompt.trim(),
        files: fileUpload.successfulFiles,
        dataSurface,
      })
    );
  };

  const startTemplateRule = (template: AgenticLogicTemplateSummaryDto) => {
    openChat({
      entry: { kind: "template", template },
      initialInput: {
        type: "text",
        content: `Start from the ${template.title} template`,
      },
    });
  };

  const canBuild =
    !fileUpload.hasUploadsInProgress &&
    !fileUpload.hasErrors &&
    (prompt.trim().length > 0 || fileUpload.successfulFiles.length > 0);

  const formProps = {
    prompt,
    onPromptChange: setPrompt,
    onBuild: startCodeRule,
    onSelectTemplate: startTemplateRule,
    templates: templatesQuery.data?.templates ?? [],
    templatesLoading: templatesQuery.isLoading,
    templatesError: templatesQuery.isError,
    onReloadTemplates: () => void templatesQuery.refetch(),
    canBuild,
    ruleIdeas: ruleIdeas.ideas,
    attachmentControls: <IntakeAttachmentButton />,
    attachmentPreview: <IntakeAttachmentPreview fileUpload={fileUpload} />,
  };

  /** The same flow in whichever frame this presentation asks for. */
  const renderFramedForm = () => {
    switch (presentation.kind) {
      case "dialog":
        return (
          <CreateRuleDialogView
            open={presentation.open}
            onClose={presentation.onClose}
            {...formProps}
          />
        );
      case "embedded":
        return <CreateRuleTabEmbeddedView {...formProps} />;
      // `page` by name, and the fallback for anything else: the ternary this
      // replaced ended on the page view, so a presentation arriving from
      // untyped code still renders the flow rather than nothing at all.
      case "page":
      default:
        return <CreateRuleTabView {...formProps} />;
    }
  };

  return (
    <>
      {inDialog || isEmbedded ? null : (
        <DiscardChangesDialog
          open={leaveGuard.isBlocked}
          mode={exitMode ?? "leave"}
          entityLabel="rule"
          leaveSubtitle="This rule has not been created yet. You can leave and start again anytime."
          onKeepEditing={leaveGuard.reset}
          onDiscard={leaveGuard.proceed}
        />
      )}
      {renderFramedForm()}
    </>
  );
};

const IntakeAttachmentButton = () => {
  const attachments = useProviderAttachments();
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      attachments.add(event.target.files);
    }
    event.target.value = "";
  };

  return (
    <>
      <input
        ref={inputRef}
        aria-label="Rule attachments"
        type="file"
        hidden
        multiple
        accept={listAllowedChatAttachmentMimes().join(",")}
        onChange={handleFiles}
      />
      <IntakeAttachmentButtonView onClick={() => inputRef.current?.click()} />
    </>
  );
};

export const IntakeAttachmentButtonView = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <IgIconButton
    aria-label="Attach a file"
    onClick={onClick}
    sx={{ borderRadius: "6px", height: 32, width: 32 }}
  >
    <Paperclip size={16} />
  </IgIconButton>
);

/** What an attachment chip says about its upload beyond the filename. */
const uploadSuffix = (
  entry: ReturnType<typeof useFileUpload>["entries"][number] | undefined
): string => {
  if (entry?.status === "error") {
    return ` — ${entry.error}`;
  }
  if (entry?.status === "uploaded") {
    return "";
  }
  return " — uploading";
};

const IntakeAttachmentPreview = ({
  fileUpload,
}: {
  fileUpload: ReturnType<typeof useFileUpload>;
}) => {
  const attachments = useProviderAttachments();
  if (attachments.files.length === 0) {
    return null;
  }
  const entries = new Map(fileUpload.entries.map((entry) => [entry.id, entry]));

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} aria-live="polite">
      {attachments.files.map((file) => {
        const entry = entries.get(file.id);
        return (
          <IgChip
            key={file.id}
            size="small"
            label={`${file.filename ?? "attachment"}${uploadSuffix(entry)}`}
            onDelete={() => attachments.remove(file.id)}
          />
        );
      })}
    </Stack>
  );
};
